/**
 * StreamX `/streamx/live` — Cinematic OTT-grade live broadcast discovery.
 * StreamX Sprint 3c rebuild.
 * ------------------------------------------------------------------
 * Doctrine:
 *   • Obsidian #0A0A0A base · glass cards (white/[0.02] + white/[0.08])
 *   • Single-stream feature card (asymmetric, dominant) when only 1 live
 *   • Asymmetric bento grid (one large + two compact) when 2-3 live
 *   • Standard responsive grid when 4+ live
 *   • Sport filter chips (subtle tint pills, no neon)
 *   • Empty state: cinematic "The Arena is Quiet" full-bleed
 *   • Motion: 700ms hover lift · 2.8s slow pulse on LIVE dots · zero gaming neon
 *
 * Data:
 *   • GET /api/livex/streams/live  → list of currently-live streams
 *   • Stream → /streamx/watch/:id
 *
 * Legacy /livex routes still work — this surface is dual-mounted.
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import {
  Radio, ArrowLeft, Eye, AlertTriangle, Sparkles, Tv, Play, Users,
} from 'lucide-react';
import { STREAMX_BRAND } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Visual primitives ────────────────────────────────────────────────

const SPORT_TINT = {
  football: { bg: 'rgba(59,130,246,0.10)', text: '#93c5fd', border: 'rgba(59,130,246,0.30)' },
  cricket: { bg: 'rgba(16,185,129,0.10)', text: '#6ee7b7', border: 'rgba(16,185,129,0.30)' },
  basketball: { bg: 'rgba(249,115,22,0.10)', text: '#fdba74', border: 'rgba(249,115,22,0.30)' },
  tennis: { bg: 'rgba(168,85,247,0.10)', text: '#d8b4fe', border: 'rgba(168,85,247,0.30)' },
  hockey: { bg: 'rgba(239,68,68,0.10)', text: '#fca5a5', border: 'rgba(239,68,68,0.30)' },
  badminton: { bg: 'rgba(6,182,212,0.10)', text: '#67e8f9', border: 'rgba(6,182,212,0.30)' },
  default: { bg: 'rgba(255,255,255,0.05)', text: '#e2e8f0', border: 'rgba(255,255,255,0.12)' },
};

const sportTint = (s) => SPORT_TINT[(s || '').toLowerCase()] || SPORT_TINT.default;

function LivePulseDot({ size = 'base' }) {
  const sz = size === 'lg' ? 'h-2.5 w-2.5' : 'h-2 w-2';
  return (
    <span className={`relative inline-flex ${sz}`} aria-hidden>
      <motion.span
        className="absolute inset-[-3px] rounded-full"
        animate={{ opacity: [0.0, 0.55, 0.0] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          background: 'radial-gradient(circle, rgba(255,59,48,0.65), rgba(255,59,48,0.0) 70%)',
          filter: 'blur(3px)',
        }}
      />
      <motion.span
        className={`absolute inline-flex ${sz} rounded-full`}
        animate={{ opacity: [0.75, 1, 0.75], scale: [1, 1.06, 1] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: '#FF3B30' }}
      />
      <span
        className={`relative inline-flex ${sz} rounded-full`}
        style={{ background: '#FF3B30', boxShadow: '0 0 10px rgba(255,59,48,0.85)' }}
      />
    </span>
  );
}

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Cards ────────────────────────────────────────────────────────────

function FeaturedStreamCard({ s }) {
  const tint = sportTint(s.sport);
  const viewers = s.viewer_count_estimate || s.viewer_count || 0;
  return (
    <Link
      to={`/streamx/watch/${s.id}`}
      data-testid={`streamx-live-featured-${s.id}`}
      className="group relative block overflow-hidden rounded-3xl"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
    >
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
        {/* Thumbnail */}
        <div
          className="relative aspect-video lg:aspect-auto lg:min-h-[420px] overflow-hidden"
          style={{
            background: `radial-gradient(60% 60% at 35% 40%, ${tint.text}28 0%, #0A0A0A 70%), linear-gradient(135deg, #14080A 0%, #0A0A0A 100%)`,
          }}
        >
          {/* Cinematic spotlight orb */}
          <div
            className="absolute -inset-4 opacity-60"
            style={{
              background: `radial-gradient(circle at 30% 50%, ${tint.text}18 0%, transparent 60%)`,
              filter: 'blur(40px)',
            }}
          />
          <div className="absolute inset-0 grid place-items-center">
            <motion.div
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="h-24 w-24 rounded-full grid place-items-center"
              style={{
                background: 'linear-gradient(135deg, rgba(255,59,48,0.95), rgba(153,0,0,0.95))',
                boxShadow: '0 20px 60px rgba(255,59,48,0.45)',
              }}
            >
              <Play className="h-9 w-9 text-white ml-1" fill="currentColor" />
            </motion.div>
          </div>
          {/* Bottom gradient + meta */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-white"
              style={{
                background: 'rgba(255,59,48,0.18)',
                border: '1px solid rgba(255,59,48,0.45)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <LivePulseDot /> LIVE NOW
            </span>
          </div>
          <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white tabular-nums"
            style={{
              background: 'rgba(0,0,0,0.55)',
              border: '1px solid rgba(255,255,255,0.10)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <Eye className="h-3 w-3" /> {viewers.toLocaleString('en-IN')}
          </div>
        </div>
        {/* Meta panel */}
        <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center gap-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">
            FEATURED · STREAMING NOW
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            {s.title}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            {s.sport && (
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wider"
                style={{
                  background: tint.bg,
                  color: tint.text,
                  border: `1px solid ${tint.border}`,
                }}
              >
                {s.sport}
              </span>
            )}
            <span className="text-sm text-white/60 font-medium">{s.academy_name || 'StreamX Academy'}</span>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <span
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white tracking-wide group-hover:shadow-[0_0_24px_rgba(255,59,48,0.4)] transition-all duration-500"
              style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)' }}
            >
              <Play className="h-4 w-4" fill="currentColor" /> Watch broadcast
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function LiveCard({ s, size = 'md' }) {
  const tint = sportTint(s.sport);
  const viewers = s.viewer_count_estimate || s.viewer_count || 0;
  const aspect = size === 'lg' ? 'aspect-[16/10]' : 'aspect-video';
  return (
    <Link
      to={`/streamx/watch/${s.id}`}
      data-testid={`streamx-live-card-${s.id}`}
      className="group block relative overflow-hidden rounded-2xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-[0_24px_64px_rgba(255,59,48,0.18)]"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
    >
      <div
        className={`relative ${aspect} overflow-hidden`}
        style={{
          background: `radial-gradient(60% 60% at 50% 45%, ${tint.text}22, #0A0A0A 75%), linear-gradient(135deg, #14080A 0%, #0A0A0A 100%)`,
        }}
      >
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="h-16 w-16 rounded-full grid place-items-center transition-transform duration-700 group-hover:scale-110"
            style={{
              background: 'linear-gradient(135deg, rgba(255,59,48,0.92), rgba(153,0,0,0.92))',
              boxShadow: '0 16px 40px rgba(255,59,48,0.35)',
            }}
          >
            <Play className="h-6 w-6 text-white ml-0.5" fill="currentColor" />
          </motion.div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />
        {/* LIVE pill */}
        <div className="absolute top-3 left-3">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white"
            style={{
              background: 'rgba(255,59,48,0.18)',
              border: '1px solid rgba(255,59,48,0.45)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <LivePulseDot /> LIVE
          </span>
        </div>
        {/* Viewer pill */}
        <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white tabular-nums"
          style={{
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.10)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Eye className="h-3 w-3" /> {viewers.toLocaleString('en-IN')}
        </div>
      </div>
      {/* Body */}
      <div className="p-4 sm:p-5">
        <h3 className="text-base sm:text-lg font-semibold tracking-tight text-white truncate leading-tight">
          {s.title}
        </h3>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {s.sport && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider"
              style={{ background: tint.bg, color: tint.text, border: `1px solid ${tint.border}` }}
            >
              {s.sport}
            </span>
          )}
          <span className="text-xs text-white/55 truncate font-medium">{s.academy_name || 'StreamX Academy'}</span>
        </div>
      </div>
    </Link>
  );
}

function ShimmerCard() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl aspect-[16/12]"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <motion.div
        className="absolute inset-0"
        animate={{ x: ['-100%', '100%'] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'linear' }}
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)',
        }}
      />
    </div>
  );
}

// ── Sport filter chips ───────────────────────────────────────────────

function SportFilterRail({ sports, active, onSelect }) {
  if (sports.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap pb-4" data-testid="streamx-live-filter-rail">
      <button
        onClick={() => onSelect('all')}
        data-testid="streamx-live-filter-all"
        className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-500 ${
          active === 'all'
            ? 'text-white shadow-[0_0_20px_rgba(255,59,48,0.35)]'
            : 'text-white/55 hover:text-white'
        }`}
        style={{
          background: active === 'all'
            ? 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)'
            : 'rgba(255,255,255,0.03)',
          border: active === 'all'
            ? '1px solid rgba(255,59,48,0.45)'
            : '1px solid rgba(255,255,255,0.08)',
        }}
      >
        All sports
      </button>
      {sports.map((sp) => {
        const tint = sportTint(sp);
        const sel = active === sp;
        return (
          <button
            key={sp}
            onClick={() => onSelect(sp)}
            data-testid={`streamx-live-filter-${sp.toLowerCase()}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider capitalize transition-all duration-500 ${
              sel ? '' : 'hover:text-white'
            }`}
            style={{
              background: sel ? tint.bg : 'rgba(255,255,255,0.03)',
              color: sel ? tint.text : 'rgba(255,255,255,0.55)',
              border: `1px solid ${sel ? tint.border : 'rgba(255,255,255,0.08)'}`,
            }}
          >
            {sp}
          </button>
        );
      })}
    </div>
  );
}

// ── Empty state — "The Arena is Quiet" ───────────────────────────────

function ArenaQuietEmptyState() {
  return (
    <section
      className="relative overflow-hidden rounded-3xl min-h-[60vh] flex items-center justify-center"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
      data-testid="streamx-live-empty"
    >
      {/* Cinematic stadium lights */}
      <div className="absolute inset-0 opacity-40">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(40% 30% at 20% 30%, rgba(255,59,48,0.18) 0%, transparent 60%),' +
              'radial-gradient(40% 30% at 80% 70%, rgba(153,0,0,0.18) 0%, transparent 60%),' +
              'radial-gradient(60% 40% at 50% 100%, rgba(255,59,48,0.10) 0%, transparent 60%)',
          }}
        />
      </div>
      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="relative px-6 sm:px-12 py-16 max-w-2xl text-center">
        <Anim>
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-7"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.08)',
              backdropFilter: 'blur(24px)',
            }}
          >
            <Radio className="h-7 w-7 text-white/40" />
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight text-white leading-none">
            The Arena is Quiet.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/55 leading-relaxed max-w-lg mx-auto font-medium">
            No live broadcasts at this moment. The network is standing by — new matches
            go live every day across the {STREAMX_BRAND} network.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/streamx/replays"
              data-testid="streamx-live-empty-cta-replays"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-500"
              style={{
                background: 'linear-gradient(135deg, #FF3B30 0%, #990000 100%)',
                boxShadow: '0 12px 32px rgba(255,59,48,0.35)',
              }}
            >
              Explore Archival Replays
            </Link>
            <Link
              to="/streamx/stories"
              data-testid="streamx-live-empty-cta-stories"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold text-white/85 hover:text-white transition-all duration-500"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.10)',
                backdropFilter: 'blur(24px)',
              }}
            >
              Read this week's stories
            </Link>
          </div>
        </Anim>
      </div>
    </section>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export default function LivexLive() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSport, setActiveSport] = useState('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/livex/streams/live`)
      .then((r) => { if (active) { setStreams(r.data?.entries || []); setError(''); } })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || 'Failed to load live streams'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    document.title = `Watch Live Matches — ${STREAMX_BRAND} by Out-Play`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.setAttribute('content',
      'Watch live sports broadcasts from academies across India. Football, cricket, basketball, and more on StreamX — the OTT-grade broadcasting network for sports academies.');
  }, []);

  // Sport facet derivation (sorted, unique, present in current results)
  const availableSports = useMemo(() => {
    const set = new Set();
    streams.forEach((s) => { if (s.sport) set.add(String(s.sport).toLowerCase()); });
    return Array.from(set).sort();
  }, [streams]);

  // Filtered live streams
  const filtered = useMemo(() => {
    if (activeSport === 'all') return streams;
    return streams.filter((s) => (s.sport || '').toLowerCase() === activeSport);
  }, [streams, activeSport]);

  // Bento layout decision
  const featured = filtered[0];
  const compact = filtered.slice(1);

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: '#0A0A0A' }}
      data-testid="streamx-live-page"
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-40 border-b"
        style={{
          background: 'rgba(0,0,0,0.65)',
          borderColor: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate('/streamx')}
            data-testid="streamx-live-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to {STREAMX_BRAND}
          </button>
          <div className="text-[10px] tracking-[0.32em] font-bold text-white/45 uppercase">
            {STREAMX_BRAND} · LIVE DISCOVERY
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="streamx-live-hero">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 40% at 50% 0%, rgba(255,59,48,0.18) 0%, transparent 60%),' +
              'radial-gradient(40% 30% at 90% 30%, rgba(153,0,0,0.10) 0%, transparent 60%),' +
              '#0A0A0A',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
          <Anim>
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] text-white"
              style={{
                background: 'rgba(255,59,48,0.10)',
                border: '1px solid rgba(255,59,48,0.32)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <LivePulseDot size="lg" /> Live across the network
            </div>
            <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.02]">
              Every match,{' '}
              <span
                style={{
                  background: 'linear-gradient(120deg, #FF3B30 0%, #FF6B5C 50%, #990000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                broadcast-grade.
              </span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/55 max-w-2xl mx-auto leading-relaxed font-medium">
              {streams.length > 0
                ? `${streams.length} ${streams.length === 1 ? 'broadcast' : 'broadcasts'} are live right now across academies on ${STREAMX_BRAND}.`
                : 'Drop in any time — new broadcasts go live daily across India\'s academy network.'}
            </p>
          </Anim>
        </div>
      </section>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pb-20" data-testid="streamx-live-main">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="streamx-live-loading">
            {Array.from({ length: 6 }).map((_, i) => <ShimmerCard key={i} />)}
          </div>
        ) : error ? (
          <div
            className="p-5 rounded-2xl flex items-start gap-3 text-sm font-medium"
            style={{
              background: 'rgba(255,59,48,0.06)',
              border: '1px solid rgba(255,59,48,0.30)',
              color: '#FFB4AD',
            }}
            data-testid="streamx-live-error"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        ) : streams.length === 0 ? (
          <ArenaQuietEmptyState />
        ) : (
          <>
            <Anim>
              <SportFilterRail
                sports={availableSports}
                active={activeSport}
                onSelect={setActiveSport}
              />
            </Anim>

            {filtered.length === 0 ? (
              <div
                className="rounded-2xl p-10 text-center"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                data-testid="streamx-live-no-sport-results"
              >
                <Tv className="h-7 w-7 text-white/35 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white tracking-tight">
                  No matches in this sport right now.
                </h3>
                <p className="mt-2 text-sm text-white/55 font-medium">
                  Try a different sport — the network has broadcasts across the day.
                </p>
              </div>
            ) : filtered.length === 1 ? (
              <Anim delay={0.1}>
                <FeaturedStreamCard s={featured} />
              </Anim>
            ) : (
              <>
                <Anim delay={0.1}>
                  <FeaturedStreamCard s={featured} />
                </Anim>
                {compact.length > 0 && (
                  <div className="mt-8 sm:mt-12">
                    <Anim>
                      <div className="flex items-end justify-between mb-5 sm:mb-7">
                        <div>
                          <div className="text-[10px] font-bold tracking-[0.3em] text-white/45 uppercase mb-2">
                            More live now
                          </div>
                          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                            Other broadcasts streaming
                          </h2>
                        </div>
                        <div className="hidden sm:flex items-center gap-2 text-[11px] font-semibold text-white/45 uppercase tracking-wider">
                          <Users className="h-3.5 w-3.5" />
                          {filtered.reduce((acc, s) => acc + (s.viewer_count_estimate || s.viewer_count || 0), 0).toLocaleString('en-IN')} watching now
                        </div>
                      </div>
                    </Anim>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="streamx-live-grid">
                      {compact.map((s, i) => (
                        <Anim key={s.id} delay={0.15 + i * 0.05}>
                          <LiveCard s={s} />
                        </Anim>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>

      {/* Footer strip */}
      <section
        className="border-t"
        style={{
          background: 'rgba(0,0,0,0.4)',
          borderColor: 'rgba(255,255,255,0.06)',
        }}
        data-testid="streamx-live-footer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex items-center justify-between gap-3 flex-wrap text-[12px] font-medium text-white/45">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {STREAMX_BRAND} · Premium sports broadcasting for academies
          </div>
          <div className="flex items-center gap-5">
            <Link to="/streamx/replays" className="hover:text-white transition-colors">Replays</Link>
            <Link to="/streamx/stories" className="hover:text-white transition-colors">Stories</Link>
            <Link to="/streamx/pricing" className="hover:text-white transition-colors">Pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
